import React, { useState, useEffect, useCallback } from 'react';
import { Button, Skeleton, FormModal, Card } from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Search,
  Close,
  Email,
  Sms,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  HourglassTop,
  Refresh,
  Send,
  Visibility,
  TrendingUp,
  Person,
  CalendarMonth
} from '../../mui-icons';
import './MessageLogsPage.css';

const MessageLogsPage = () => {
  const { canEdit } = useAdminAuth();
  
  // Dashboard State
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsDays, setStatsDays] = useState(7);
  
  // List State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Detail Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [logDetail, setLogDetail] = useState(null);
  
  // Resend State
  const [resendLoading, setResendLoading] = useState(false);
  const [resendResult, setResendResult] = useState(null);

  // Load stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await adminAPI.getMessageLogsStats(statsDays);
      if (response.status === 'success') {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [statsDays]);

  // Load logs
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(channelFilter && { channel: channelFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { message_type: typeFilter }),
        ...(recipientSearch && { recipient: recipientSearch }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        order_by: 'created_at',
        order_dir: 'desc'
      };

      const response = await adminAPI.getMessageLogs(params);
      
      if (response.status === 'success') {
        setLogs(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, channelFilter, statusFilter, typeFilter, recipientSearch, dateFrom, dateTo]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Open detail modal
  const openDetail = async (log) => {
    setSelectedLog(log);
    setDetailLoading(true);
    setLogDetail(null);
    setResendResult(null);

    try {
      const response = await adminAPI.getMessageLogDetail(log.idmessagelog);
      if (response.status === 'success') {
        setLogDetail(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Close detail modal
  const closeDetail = () => {
    setSelectedLog(null);
    setLogDetail(null);
    setResendResult(null);
  };

  // Handle resend
  const handleResend = async () => {
    if (!logDetail) return;
    
    setResendLoading(true);
    setResendResult(null);

    try {
      const response = await adminAPI.resendMessage(logDetail.idmessagelog);
      setResendResult({
        success: response.status === 'success',
        message: response.message
      });
      // Reload logs to update status
      loadLogs();
    } catch (err) {
      setResendResult({
        success: false,
        message: err.message
      });
    } finally {
      setResendLoading(false);
    }
  };

  // Get channel badge
  const getChannelBadge = (channel) => {
    if (channel === 'email') {
      return <span className="channel-badge channel-email"><Email sx={{ fontSize: 14 }} /> Email</span>;
    }
    return <span className="channel-badge channel-sms"><Sms sx={{ fontSize: 14 }} /> SMS</span>;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      sent: { label: 'Enviado', class: 'status-success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
      failed: { label: 'Falhou', class: 'status-error', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
      pending: { label: 'Pendente', class: 'status-warning', icon: <HourglassTop sx={{ fontSize: 14 }} /> },
      bounced: { label: 'Retornado', class: 'status-error', icon: <ErrorIcon sx={{ fontSize: 14 }} /> }
    };
    const { label, class: className, icon } = statusMap[status] || { label: status, class: '', icon: null };
    return <span className={`status-badge ${className}`}>{icon} {label}</span>;
  };

  // Get message type label
  const getTypeLabel = (type) => {
    const typeMap = {
      magic_link: 'Magic Link',
      password_reset: 'Reset de Senha',
      welcome: 'Boas-vindas',
      notification: 'Notificação',
      sms_token: 'Token SMS',
      sms_notification: 'SMS Notificação'
    };
    return typeMap[type] || type;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  // Format short date
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="message-logs-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Email sx={{ fontSize: 32 }} />
          <div>
            <h1>Logs de Mensagens</h1>
            <p>Auditoria e reenvio de emails e SMS</p>
          </div>
        </div>

        <div className="header-actions">
          <select 
            className="stats-period-select"
            value={statsDays}
            onChange={(e) => setStatsDays(Number(e.target.value))}
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={15}>Últimos 15 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* Dashboard Cards */}
      {!statsLoading && stats && (
        <div className="stats-dashboard">
          <Card variant="stat" icon={<Send />} title="Total Enviadas" className="stats-card">
            <span className="pn-card__stat-value">{stats.summary?.total_messages || 0}</span>
          </Card>
          <Card variant="stat" icon={<CheckCircle />} title="Sucesso" className="stats-card">
            <span className="pn-card__stat-value">{stats.summary?.total_sent || 0}</span>
          </Card>
          <Card variant="stat" icon={<ErrorIcon />} title="Falhas" className="stats-card">
            <span className="pn-card__stat-value">{stats.summary?.total_failed || 0}</span>
          </Card>
          <Card variant="stat" icon={<TrendingUp />} title="Taxa de Sucesso" className="stats-card">
            <span className="pn-card__stat-value">{stats.summary?.success_rate?.toFixed(1) || 0}%</span>
          </Card>
          <Card variant="stat" icon={<Email />} title="Emails" className="stats-card">
            <span className="pn-card__stat-value">{stats.by_channel?.email || 0}</span>
          </Card>
          <Card variant="stat" icon={<Sms />} title="SMS" className="stats-card">
            <span className="pn-card__stat-value">{stats.by_channel?.sms || 0}</span>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input 
              type="text"
              placeholder="Buscar por destinatário (email/telefone)..."
              value={recipientSearch}
              onChange={(e) => {
                setRecipientSearch(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="filter-group">
            <label>Canal</label>
            <select 
              value={channelFilter}
              onChange={(e) => {
                setChannelFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select 
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="sent">Enviado</option>
              <option value="failed">Falhou</option>
              <option value="pending">Pendente</option>
              <option value="bounced">Retornado</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Tipo</label>
            <select 
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="magic_link">Magic Link</option>
              <option value="password_reset">Reset de Senha</option>
              <option value="welcome">Boas-vindas</option>
              <option value="notification">Notificação</option>
              <option value="sms_token">Token SMS</option>
            </select>
          </div>

          <div className="filter-group">
            <label>De</label>
            <input 
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="filter-group">
            <label>Até</label>
            <input 
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadLogs}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Canal</th>
                    <th>Tipo</th>
                    <th>Destinatário</th>
                    <th>Assunto</th>
                    <th>Status</th>
                    <th>Tentativas</th>
                    <th>Data</th>
                    <th style={{ width: '80px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td><Skeleton width={70} height={24} borderRadius={4} /></td>
                        <td><Skeleton width={100} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={180} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={200} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={80} height={24} borderRadius={4} /></td>
                        <td><Skeleton width={30} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={120} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={40} height={32} borderRadius={4} /></td>
                      </tr>
                    ))
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-state">
                        <Email sx={{ fontSize: 48, opacity: 0.3 }} />
                        <p>Nenhum log encontrado</p>
                      </td>
                    </tr>
                  ) : (
                    logs.map(log => (
                      <tr key={log.idmessagelog}>
                        <td>{getChannelBadge(log.channel)}</td>
                        <td>
                          <span className="type-label">{getTypeLabel(log.message_type)}</span>
                        </td>
                        <td>
                          <div className="recipient-cell">
                            <span className="recipient-value">{log.recipient}</span>
                            {log.recipient_name && (
                              <span className="recipient-name">{log.recipient_name}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="subject-cell" title={log.subject}>
                            {log.subject || '-'}
                          </span>
                        </td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td className="attempts-cell">{log.attempts || 1}</td>
                        <td>
                          <div className="date-cell">
                            <span>{formatShortDate(log.created_at)}</span>
                            <span className="date-time">{new Date(log.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="secondary"
                            className="pn-btn--icon"
                            onClick={() => openDetail(log)}
                            title="Ver detalhes"
                          >
                            <Visibility sx={{ fontSize: 18 }} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={!!selectedLog}
        onClose={closeDetail}
        title="Detalhes do Log"
        large
        footer={
          canEdit() && logDetail?.status === 'failed' ? (
            <Button
              variant="primary"
              onClick={handleResend}
              disabled={resendLoading}
              loading={resendLoading}
            >
              <Refresh sx={{ fontSize: 18 }} />
              {resendLoading ? 'Reenviando...' : 'Reenviar Mensagem'}
            </Button>
          ) : null
        }
      >
        {detailLoading ? (
          <div className="modal-loading">
            <Skeleton width="100%" height={300} borderRadius={8} />
          </div>
        ) : logDetail ? (
          <div className="message-log-detail">
                {/* Resend Result */}
                {resendResult && (
                  <div className={`resend-result ${resendResult.success ? 'success' : 'error'}`}>
                    {resendResult.success ? <CheckCircle /> : <ErrorIcon />}
                    {resendResult.message}
                  </div>
                )}

                {/* Status Summary */}
                <div className="detail-summary">
                  <div className="detail-summary-item">
                    {getChannelBadge(logDetail.channel)}
                  </div>
                  <div className="detail-summary-item">
                    {getStatusBadge(logDetail.status)}
                  </div>
                  <div className="detail-summary-item">
                    <span className="type-label">{getTypeLabel(logDetail.message_type)}</span>
                  </div>
                </div>

                {/* Detail Grid */}
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Destinatário</label>
                    <span>{logDetail.recipient}</span>
                  </div>
                  <div className="detail-item">
                    <label>Nome</label>
                    <span>{logDetail.recipient_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Assunto</label>
                    <span>{logDetail.subject || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Provider</label>
                    <span>{logDetail.provider || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tentativas</label>
                    <span>{logDetail.attempts || 1}</span>
                  </div>
                  <div className="detail-item">
                    <label>ID do Provider</label>
                    <span className="mono">{logDetail.provider_message_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Criado em</label>
                    <span>{formatDate(logDetail.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Enviado em</label>
                    <span>{formatDate(logDetail.sent_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Última Tentativa</label>
                    <span>{formatDate(logDetail.last_attempt_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Request Path</label>
                    <span className="mono">{logDetail.request_path || '-'}</span>
                  </div>
                </div>

                {/* Error Message */}
                {logDetail.error_message && (
                  <div className="error-box">
                    <label>Mensagem de Erro</label>
                    <p>{logDetail.error_message}</p>
                  </div>
                )}

                {/* Content Preview */}
                {logDetail.content_preview && (
                  <div className="content-preview">
                    <label>Prévia do Conteúdo</label>
                    <p>{logDetail.content_preview}</p>
                  </div>
                )}

                {/* User/Influencer Info */}
                {(logDetail.user || logDetail.influencer) && (
                  <div className="related-info">
                    <label>Relacionado a</label>
                    {logDetail.user && (
                      <div className="related-item">
                        <Person sx={{ fontSize: 16 }} />
                        <span>Usuário: {logDetail.user.name} ({logDetail.user.mail})</span>
                      </div>
                    )}
                    {logDetail.influencer && (
                      <div className="related-item">
                        <Person sx={{ fontSize: 16 }} />
                        <span>Influenciador: {logDetail.influencer.name} ({logDetail.influencer.email})</span>
                      </div>
                    )}
                  </div>
                )}

          </div>
        ) : (
          <div className="modal-error">
            <Warning sx={{ fontSize: 48 }} />
            <p>Erro ao carregar detalhes</p>
          </div>
        )}
      </FormModal>
    </div>
  );
};

export default MessageLogsPage;

